import { action, computed, observable, reaction, runInAction } from 'mobx';
import { toast } from 'react-toastify';

import { history } from '../..';
import agent from '../api/agent';
import { getOptions, PROCESS_STATE } from '../common/util/util';
import { ICheckUploadFileResult } from '../models/checkUploadFileResult';
import {
  IDepositSalesTicket,
  IDepositSalesTicketFilters,
} from '../models/depositSalesTicket';
import { IOption } from '../models/option';
import { RootStore } from './rootStore';

export default class DepositSalesTicketStore {
  rootStore: RootStore;
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    reaction(
      () => this.depositSalesTicketFilters,
      () => {
        this.depositSalesTicketsRegistry.clear();
        this.loadDepositSalesTickets();
      }
    );
  }

  @observable mdTdTicketTypeOptions: IOption[] = [];
  @observable ticketReserveStatusOptions: IOption[] = [];
  @observable ticketReserveTypeOptions: IOption[] = [];
  @observable depositSalesTicketsCount = 0;
  @observable depositSalesTicketsRegistry = new Map();
  @observable depositSalesTicket: IDepositSalesTicket | null = null;
  @observable
  depositSalesTicketFilters: IDepositSalesTicketFilters | null = null;
  @observable loading = false;
  @observable submitting = false;
  @observable searchMode = true;
  @observable checkUploadFileResults: ICheckUploadFileResult[] = [];
  @observable depositSalesTicketPrintsRegistry = new Map();

  @computed get checkUploadFileSuccessfulResults() {
    const results = this.checkUploadFileResults.filter(
      (a) => a.errorMessage === null
    );
    return results;
  }

  @computed get checkUploadFileErrorResults() {
    const results = this.checkUploadFileResults.filter(
      (a) => a.errorMessage !== null
    );
    return results;
  }

  @action clearCheckUploadFileResults = () => {
    this.checkUploadFileResults = [];
  };

  @computed get depositSalesTickets() {
    return Array.from(this.depositSalesTicketsRegistry.values()).sort(
      (a, b) => Date.parse(b.startDate) - Date.parse(a.startDate)
    );
  }

  @computed get depositSalesTicketPrints() {
    return Array.from(this.depositSalesTicketPrintsRegistry.values()).sort(
      (a, b) =>
        Date.parse(b.ticketReserveTime) - Date.parse(a.ticketReserveTime)
    );
  }

  @computed get mdTdTicketTypeFilterOptions() {
    const options = Array.from(this.mdTdTicketTypeOptions.values());
    options.unshift({ key: '', value: '', text: '' });
    return options;
  }

  @computed get ticketReserveStatusFilterOptions() {
    const options = Array.from(this.ticketReserveStatusOptions.values());
    options.unshift({ key: '', value: '', text: '' });
    return options;
  }

  @computed get ticketReserveTypeFilterOptions() {
    const options = Array.from(this.ticketReserveTypeOptions.values());
    options.unshift({ key: '', value: '', text: '' });
    return options;
  }

  @action setSearchMode = (searchMode: boolean) => {
    this.searchMode = searchMode;
  };

  @action setDepositSalesTicketFilters = (
    depositSalesTicketFilters: IDepositSalesTicketFilters
  ) => {
    this.depositSalesTicketFilters = depositSalesTicketFilters;
  };

  @action loadDepositSalesTicketPrints = async () => {
    this.loading = true;
    try {
      const depositSalesTicketPrints = await agent.DepositSalesTickets.listPrint();
      runInAction('loading depositSalesTicketPrints', () => {
        depositSalesTicketPrints.forEach((depositSalesTicketPrints) => {
          this.depositSalesTicketPrintsRegistry.set(
            depositSalesTicketPrints.depositSalesTicketPrintId,
            depositSalesTicketPrints
          );
        });
        this.loading = false;
      });
    } catch (error) {
      runInAction('load depositSalesTicketPrints error', () => {
        this.loading = false;
      });
    }
  };

  @action loadDepositSalesTicketAttributes = async () => {
    this.loading = true;
    try {
      const attributes = await agent.DepositSalesTickets.attributes();
      runInAction('loading depositSalesTicket attributes', () => {
        this.mdTdTicketTypeOptions = getOptions(attributes.mdTdTicketTypes);
        this.ticketReserveStatusOptions = getOptions(
          attributes.ticketReserveStatuses
        );
        this.ticketReserveTypeOptions = getOptions(
          attributes.ticketReserveTypes
        );
        this.loading = false;
      });
    } catch (error) {
      runInAction('load depositSalesTicket attributes error', () => {
        this.loading = false;
      });
    }
  };

  @action loadDepositSalesTickets = async () => {
    this.loading = true;
    try {
      const depositSalesTickets = await agent.DepositSalesTickets.list(
        this.depositSalesTicketFilters!
      );
      runInAction('loading depositSalesTickets', () => {
        depositSalesTickets.forEach((depositSalesTicket) => {
          this.depositSalesTicketsRegistry.set(
            depositSalesTicket.salesId,
            depositSalesTicket
          );
        });
        this.depositSalesTicketsCount = depositSalesTickets.length;
        this.loading = false;
      });
    } catch (error) {
      runInAction('load depositSalesTickets error', () => {
        this.loading = false;
      });
      error.response &&
        error.response.data &&
        error.response.data.errors &&
        toast.error(Object.values(error.response.data.errors).flat().join());
    }
  };

  @action loadDepositSalesTicket = async (key: string) => {
    let depositSalesTicket = this.depositSalesTicketsRegistry.get(key);
    if (depositSalesTicket) {
      this.depositSalesTicket = depositSalesTicket;
      return depositSalesTicket;
    } else {
      this.loading = true;
      try {
        depositSalesTicket = await agent.DepositSalesTickets.details(key);
        runInAction('getting depositSalesTicket', () => {
          this.depositSalesTicket = depositSalesTicket;
          this.loading = false;
        });
        return depositSalesTicket;
      } catch (error) {
        runInAction('get depositSalesTicket error', () => {
          this.loading = false;
        });
        console.log(error);
      }
    }
  };

  @action clearDepositSalesTicket = () => {
    this.depositSalesTicket = null;
  };

  @action createDepositSalesTicket = async (
    depositSalesTicket: IDepositSalesTicket
  ) => {
    this.submitting = true;
    try {
      await agent.DepositSalesTickets.create(depositSalesTicket);
      runInAction('creating depositSalesTicket', () => {
        this.submitting = false;
      });
      this.depositSalesTicketsRegistry.clear();
      this.loadDepositSalesTickets();
      history.push('/admin/depositsales');
    } catch (error) {
      runInAction('create depositSalesTicket error', () => {
        this.submitting = false;
      });
      toast.error('登録処理でエラーが発生しました');
      console.log(error);
    }
  };

  @action editDepositSalesTicket = async (
    depositSalesTicket: IDepositSalesTicket
  ) => {
    this.submitting = true;
    try {
      await agent.DepositSalesTickets.update(depositSalesTicket);
      runInAction('editing depositSalesTicket', () => {
        this.submitting = false;
      });
      this.depositSalesTicketsRegistry.clear();
      this.loadDepositSalesTickets();
      history.push('/admin/depositsales');
    } catch (error) {
      runInAction('edit depositSalesTicket error', () => {
        this.submitting = false;
      });
      error.response &&
        error.response.data &&
        error.response.data.errors &&
        toast.error(Object.values(error.response.data.errors).flat().join());
    }
  };

  @action changeLimitDepositSalesTicket = async (
    depositSalesTicket: IDepositSalesTicket
  ) => {
    this.submitting = true;
    try {
      await agent.DepositSalesTickets.changeLimit(depositSalesTicket);
      runInAction('changing TimeLimit depositSalesTicket', () => {
        this.submitting = false;
      });
      this.depositSalesTicketsRegistry.clear();
      this.loadDepositSalesTickets();
      history.push('/admin/depositsales');
    } catch (error) {
      runInAction('changing TimeLimit depositSalesTicket error', () => {
        this.submitting = false;
      });
      toast.error('有効期限日の変更処理でエラーが発生しました');
      console.log(error);
    }
  };

  @action deleteDepositSalesTicket = async (
    depositSalesTicket: IDepositSalesTicket
  ) => {
    this.submitting = true;
    try {
      await agent.DepositSalesTickets.delete(depositSalesTicket.salesId);
      runInAction('deleting depositSalesTicket', () => {
        depositSalesTicket.deleteFlagEnum = PROCESS_STATE.Done;
        this.depositSalesTicketsRegistry.set(
          depositSalesTicket.salesId,
          depositSalesTicket
        );
        this.submitting = false;
      });
    } catch (error) {
      runInAction('delete depositSalesTicket error', () => {
        this.submitting = false;
      });
      toast.error('削除処理でエラーが発生しました');
      console.log(error);
    }
  };

  @action issueDepositSalesTicket = async (
    depositSalesTicket: IDepositSalesTicket
  ) => {
    this.submitting = true;
    try {
      await agent.DepositSalesTickets.issue(depositSalesTicket.salesId);
      runInAction('issuing depositSalesTicket', () => {
        depositSalesTicket.ticketReserveStatusEnum = PROCESS_STATE.Done;
        depositSalesTicket.ticketReserveTime = new Date();
        this.depositSalesTicketsRegistry.set(
          depositSalesTicket.salesId,
          depositSalesTicket
        );
        this.submitting = false;
      });
    } catch (error) {
      runInAction('issuing depositSalesTicket error', () => {
        this.submitting = false;
      });
      toast.error('発券処理でエラーが発生しました');
      console.log(error);
    }
  };

  @action checkUploadFile = async (file: File) => {
    this.loading = true;
    try {
      const checkUploadFileResults = await agent.DepositSalesTickets.checkUploadFile(
        file
      );
      runInAction(() => {
        this.checkUploadFileResults = checkUploadFileResults;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      toast.error('正しいファイル形式でアップロードしてください');
      console.log(error);
    }
  };

  @action completeUploadFile = async (
    checkUploadFileSuccessfulResults: ICheckUploadFileResult[]
  ) => {
    this.submitting = true;
    try {
      await agent.DepositSalesTickets.completeUploadFile(
        checkUploadFileSuccessfulResults
      );
      runInAction(() => {
        this.submitting = false;
      });
      this.depositSalesTicketsRegistry.clear();
      this.loadDepositSalesTickets();
      history.push('/admin/depositsales');
    } catch (error) {
      runInAction(() => {
        this.submitting = false;
      });
      toast.error('アップロードデータ登録処理でエラーが発生しました');
      console.log(error);
    }
  };
}
